import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, ChevronLeft, ChevronRight, Quote } from 'lucide-react';
import { TESTIMONIALS } from '../../data/content';

export const Testimonials = () => {
  const [active, setActive] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setActive((p) => (p + 1) % TESTIMONIALS.length), 7000);
    return () => clearInterval(t);
  }, []);

  const t = TESTIMONIALS[active];

  return (
    <section
      id="voices"
      className="relative py-24 md:py-40 bg-[#F9F7F4] overflow-hidden"
      data-testid="testimonials-section"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="grid md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="flex items-center gap-3 mb-6"
            >
              <div className="h-px w-12 bg-[#0D1B2A]" />
              <span className="text-[#0D1B2A] text-xs tracking-[0.4em] uppercase">
                Voices
              </span>
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="font-serif text-[#0D1B2A] leading-[1] tracking-tight mb-8"
              style={{ fontSize: 'clamp(2.25rem, 5vw, 4rem)' }}
            >
              Words from
              <br />
              <span className="italic font-light">our clients.</span>
            </motion.h2>

            <div className="flex items-center gap-3">
              <button
                onClick={() =>
                  setActive((p) => (p === 0 ? TESTIMONIALS.length - 1 : p - 1))
                }
                className="w-12 h-12 border border-[#0D1B2A]/20 hover:border-[#C9A84C] hover:bg-[#C9A84C] hover:text-white transition-all flex items-center justify-center"
                data-testid="testimonial-prev"
                aria-label="Previous"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => setActive((p) => (p + 1) % TESTIMONIALS.length)}
                className="w-12 h-12 border border-[#0D1B2A]/20 hover:border-[#C9A84C] hover:bg-[#C9A84C] hover:text-white transition-all flex items-center justify-center"
                data-testid="testimonial-next"
                aria-label="Next"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
              <div className="ml-4 text-xs tracking-wider text-[#0D1B2A]/40 tabular-nums">
                {String(active + 1).padStart(2, '0')} / {String(TESTIMONIALS.length).padStart(2, '0')}
              </div>
            </div>
          </div>

          <div className="md:col-span-7 md:col-start-6 relative min-h-[320px]">
            <Quote className="absolute -top-4 -left-2 w-20 h-20 text-[#C9A84C]/15" />
            <AnimatePresence mode="wait">
              <motion.div
                key={t.name}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -30 }}
                transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                className="relative"
                data-testid="active-testimonial"
              >
                <div className="flex gap-1 mb-6">
                  {[...Array(t.rating)].map((_, i) => (
                    <Star key={`${t.name}-star-${i}`} className="w-4 h-4 fill-[#C9A84C] text-[#C9A84C]" />
                  ))}
                </div>
                <p
                  className="font-serif text-[#0D1B2A] leading-[1.3] tracking-tight"
                  style={{ fontSize: 'clamp(1.5rem, 3vw, 2.5rem)' }}
                >
                  "{t.text}"
                </p>
                <div className="flex items-center gap-4 mt-10 pt-8 border-t border-[#0D1B2A]/10">
                  <div className="w-12 h-12 rounded-full bg-[#0D1B2A] text-[#C9A84C] flex items-center justify-center font-serif text-sm">
                    {t.initials}
                  </div>
                  <div>
                    <div className="font-medium text-[#0D1B2A]">{t.name}</div>
                    <div className="text-sm text-[#0D1B2A]/50">{t.title}</div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};
